import Button from "@common/components/Button";
import isVehicleListingPage, {
  VLP_PATH,
} from "@common/helpers/isVehicleListingPage";
import {
  FilterActions,
  VehicleMakeFilterState,
} from "@modules/vehicle/types/vehicleFilter";
import VehicleFilterContext from "@modules/vehicle/VehicleFilterContext";
import classNames from "classnames";
import { useTranslation } from "next-i18next";
import Link from "next/link";
import { useRouter } from "next/router";
import { useContext, useState } from "react";
import getMakeIcon from "@modules/vehicle/helpers/getMakeIcon";
import cleanVlpQueryParam from "@modules/vehicle/helpers/cleanVlpQueryParam";

type Props = {
  brands: string[] | undefined;
};

interface Orderable {
  order: number;
}

const initialBrandsToShow = 10;

const brandsToDisplay: {
  [name: string]: { order: number };
} = {};
brandsToDisplay.Chevrolet = { order: 1 };
brandsToDisplay.Dodge = { order: 2 };
brandsToDisplay.Ford = { order: 3 };
brandsToDisplay.Honda = { order: 4 };
brandsToDisplay.Hyundai = { order: 5 };
brandsToDisplay.Jeep = { order: 6 };
brandsToDisplay.Kia = { order: 7 };
brandsToDisplay.Mazda = { order: 8 };
brandsToDisplay.Nissan = { order: 9 };
brandsToDisplay.Ram = { order: 10 };
brandsToDisplay.Toyota = { order: 11 };

const sortDisplayOrder = (brands: string[] | undefined) => {
  return brands?.map((brandName) => {
    const currentBrand = brandsToDisplay[brandName];
    return {
      order: currentBrand?.order || 12,
      name: brandName,
    };
  });
};

const ShopPopularBrands = ({ brands }: Props) => {
  const { t } = useTranslation(["common"]);
  const { pathname } = useRouter();
  const { dispatch } = useContext(VehicleFilterContext);

  const [viewAllMakes, setViewAllMakes] = useState<boolean>(false);

  const styles = {
    heading: "body-1",
    icon: "m-auto w-24 h-12 xl:w-20",
    cardsContainer: "md:grid-cols-4 lg:grid-cols-6",
    title: "",
  };

  // This adds the "make" selection in to our filters when user interacts within VLP.
  const onSelectAction = (makeState: VehicleMakeFilterState) => {
    if (isVehicleListingPage(pathname)) {
      dispatch({
        type: FilterActions.AddMakeFilter,
        payload: makeState,
      });
    }
  };

  const brandCards = sortDisplayOrder(brands);

  return (
    <div>
      <div className={`${styles.heading} pt-6 pb-4`}>{t("common:brand")}</div>
      <div className={`${styles.cardsContainer} body-3 grid grid-cols-3 gap-2`}>
        {brandCards
          ?.sort((a: Orderable, b: Orderable) => a.order - b.order)
          .map((brand, i) => {
            const showMake = i <= initialBrandsToShow;
            const slug = `/${VLP_PATH}/${cleanVlpQueryParam(brand.name)}`;
            const BrandIcon = getMakeIcon(brand.name);

            return (
              brand && (
                <Link
                  href={slug}
                  key={`link-body-style-${brand.name}`}
                  className={classNames({
                    block: showMake && viewAllMakes,
                    hidden: !showMake && !viewAllMakes,
                  })}
                >
                  <Button
                    buttonStyle="filter"
                    fill="outline"
                    spacing="tight-hug"
                    className="rounded-[4px] active:bg-gray-25 hover:border-cyan-400"
                    onClick={() => onSelectAction({ make: brand.name })}
                  >
                    <div className={`${styles.title} py-1`}>
                      <BrandIcon
                        className={`${styles.icon} m-auto text-gray-100`}
                      />
                      {brand.name}
                    </div>
                  </Button>
                </Link>
              )
            );
          })}

        {!viewAllMakes && (
          <Button
            buttonStyle="filter"
            fill="outline"
            spacing="tight-hug"
            className="rounded-[4px] active:bg-gray-25 hover:border-cyan-400"
            onClick={() => setViewAllMakes(true)}
          >
            <div className={`${styles.title} whitespace-nowrap py-1 relative`}>
              <span className="absolute top-1/2 text-primary-bold translate-y-[-50%]">
                {t("common:view_all")}
              </span>
              <span className="invisible">{t("common:view_all")}</span>
            </div>
          </Button>
        )}
      </div>
    </div>
  );
};

export default ShopPopularBrands;
